import {useCallback, useEffect, useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {BarChart3, CheckSquare, Users} from 'lucide-react'
import {useNavigate, useLocation, useSearchParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {buildFilterQuery} from '@/lib/build-filter-query'
import {CertificationReviewControllerMyReviewItemsEndpoint} from '@/types/endpoint-contracts'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Card, CardContent} from '@/components/ui/card'
import {Checkbox} from '@/components/ui/checkbox'
import {Progress} from '@/components/ui/progress'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {SortableHeader} from '@/components/ui/sortable-header'
import {Pagination} from '@/components/ui/pagination'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Combobox} from '@/components/ui/combobox'
import type {ComboboxOption} from '@/components/ui/combobox'
import {ErrorBanner} from '@/components/ui/error-banner'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {formatDateShort} from '@/lib/date-utils'
import {Icon} from '@/components/shared/Icon'
import {
    ReviewItemStatus,
    CertificationReviewItemResponseFields,
} from '@/types/certification'
import type {
    CertificationReviewItem,
} from '@/types/certification'
import {EmptyState} from '@/components/shared/EmptyState'
import {useIsEmbedded} from '@/components/shared/EmbeddedContext'
import {PageBackButton} from '@/components/shared/PageBackButton'
import {PageSectionHeader} from '@/components/shared/PageSectionHeader'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {PillButton} from '@/components/shared/PillButton'
import {MasterDetailLayout} from '@/components/shared/MasterDetailLayout'
import {FilterToolbar} from '@/components/shared/FilterToolbar'
import {BulkActionBar} from '@/components/shared/BulkActionBar'
import {ScrollableBox} from '@/components/shared/ScrollableBox'

function isFullyDecided(item: CertificationReviewItem) {
    return item.totalAssignments > 0 && item.decidedAssignments === item.totalAssignments
}

const ROW_SELECTED_CLASS = 'cursor-pointer bg-muted'
const ROW_UNSELECTED_CLASS = 'cursor-pointer'

import {ReviewItemStatusBadge} from '@/types/certification'

export function CertificationReviews() {
    const i18n = useI18n()
    const embedded = useIsEmbedded()
    const services = useServices()
    usePageTitle(i18n.certificationReviews.pageTitle)
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()

    // Read highlight item from location state (set when navigating back from AssignmentDecisions).
    // campaignId is mirrored to the URL so deep-links + browser-back keep the
    // selected campaign — the location-state path stays as a fallback for
    // pre-existing call sites that pass the id only via state.
    const locationState = location.state as {highlightItemId?: string; campaignId?: string} | null
    const highlightItemId = locationState?.highlightItemId ?? null
    const restoredCampaignId = searchParams.get(CertificationReviewItemResponseFields.campaignId) ?? locationState?.campaignId ?? null

    const {
        page, pageSize, sortBy, sortDir, queryString,
        setSort, setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: CertificationReviewControllerMyReviewItemsEndpoint,
        defaultSortBy: CertificationReviewControllerMyReviewItemsEndpoint.sort.IDENTITY_DISPLAY_NAME,
        defaultSortDir: SortDir.ASC,
        sortable: true,
    })

    const [selectedCampaignId, setSelectedCampaignIdState] = useState<string | null>(restoredCampaignId)
    const setSelectedCampaignId = useCallback((id: string | null) => {
        setSelectedCampaignIdState(id)
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            if (id) next.set(CertificationReviewItemResponseFields.campaignId, id)
            else next.delete(CertificationReviewItemResponseFields.campaignId)
            return next
        }, {replace: true})
    }, [setSearchParams])
    const [selectedItem, setSelectedItem] = useState<CertificationReviewItem | null>(null)
    const certifyDialog = useDialog()
    const reassignDialog = useDialog()
    const [reassignIdentityId, setReassignIdentityId] = useState<string | null>(null)
    const [identitySearchQuery, setIdentitySearchQuery] = useState('')
    const [checkedItemIds, setCheckedItemIds] = useState<Set<string>>(new Set())
    const bulkCertifyDialog = useDialog()

    // Fetch distinct campaigns for this reviewer
    const {data: campaigns} = useQuery({
        queryKey: ['certification-my-campaigns'],
        queryFn: () => services.certification.myCampaigns(),
    })

    // Auto-select first campaign, or advance to next if current campaign is no longer in the list
    useEffect(() => {
        if (!campaigns || campaigns.length === 0) return
        if (!selectedCampaignId || !campaigns.some(c => c.id === selectedCampaignId)) {
            setSelectedCampaignId(campaigns[0].id)
        }
    }, [campaigns, selectedCampaignId, setSelectedCampaignId])

    // Reset selection when campaign changes
    useEffect(() => {
        setCheckedItemIds(new Set())
        setSelectedItem(null)
        setPage(0)
    }, [selectedCampaignId]) // eslint-disable-line react-hooks/exhaustive-deps

    // Clear checked items when page/filter/sort changes
    useEffect(() => {
        setCheckedItemIds(new Set())
    }, [page, searchAndFilters, sortBy, sortDir])

    // Compose URL query for the backend list call: the unified-search bar already
    // owns q + bar-driven filters via queryString; the campaign pill bar lives
    // outside the bar (page-specific UX), so its selection is appended via the
    // typed buildFilterQuery helper to keep the contract as the single source.
    const fullQueryString = useMemo(() => {
        if (!selectedCampaignId) return queryString
        const campaignParam = buildFilterQuery(
            CertificationReviewControllerMyReviewItemsEndpoint,
            {CAMPAIGN_ID: selectedCampaignId})
        return queryString ? `${queryString}&${campaignParam}` : campaignParam
    }, [queryString, selectedCampaignId])

    const {data: reviewPage, isLoading, error} = useQuery({
        queryKey: ['certification-reviews-list', selectedCampaignId, fullQueryString],
        queryFn: () => services.certification.listMyReviews(Object.fromEntries(new URLSearchParams(fullQueryString))),
        enabled: !!selectedCampaignId,
    })

    const reviewItems = reviewPage?.content ?? []
    const totalPages = reviewPage?.totalPages ?? 0
    const totalElements = reviewPage?.totalElements ?? 0
    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const certifyMutation = useEntityMutation({
        mutationFn: (reviewItemId: string) =>
            services.certification.certify(reviewItemId, {}),
        invalidate: [['certification-my-reviews'], ['certification-my-campaigns']],
        successMessage: i18n.certificationReviews.certify,
        closeDialog: certifyDialog.close,
        onSuccess: () => {
            setSelectedItem(null)
        },
    })

    const bulkCertifyMutation = useEntityMutation({
        mutationFn: (reviewItemIds: string[]) =>
            services.certification.bulkCertify({reviewItemIds}),
        invalidate: [['certification-my-reviews'], ['certification-my-campaigns']],
        successMessage: i18n.certificationReviews.bulkCertifySuccess({count: checkedItemIds.size}),
        closeDialog: bulkCertifyDialog.close,
        onSuccess: () => {
            setCheckedItemIds(new Set())
            setSelectedItem(null)
        },
    })

    // Identity search for reassign dialog
    const {data: identitySearchData, isLoading: identitySearchLoading} = useQuery({
        queryKey: ['identities', 'search', identitySearchQuery],
         
        queryFn: () => services.identities.list({q: identitySearchQuery, size: 10}),
        
    })
    const identityOptions: ComboboxOption[] = (identitySearchData?.content || []).map((identity) => ({
        value: identity.id,
        label: identity.party.displayName,
        description: identity.identityType,
    }))

    const reassignMutation = useEntityMutation({
        mutationFn: ({reviewItemId, newReviewerIdentityId}: { reviewItemId: string; newReviewerIdentityId: string }) =>
            services.certification.reassign(reviewItemId, {newReviewerIdentityId}),
        invalidate: [['certification-my-reviews'], ['certification-my-campaigns']],
        successMessage: i18n.certificationReviews.reassignSuccess,
        closeDialog: reassignDialog.close,
        onSuccess: () => {
            setReassignIdentityId(null)
            setIdentitySearchQuery('')
        },
    })

    // Sync selectedItem with fresh query data and auto-select first item
    // When returning from AssignmentDecisions, prefer the highlighted item
    useEffect(() => {
        if (reviewItems.length === 0) {
            setSelectedItem(null)
            return
        }
        // If returning from assignments page, select the highlighted item
        if (highlightItemId) {
            const highlighted = reviewItems.find(item => item.id === highlightItemId)
            if (highlighted) {
                setSelectedItem(highlighted)
                // Clear the location state so it doesn't persist on subsequent navigations
                navigate(location.pathname + location.search, {replace: true, state: {}})
                return
            }
        }
        if (!selectedItem) {
            setSelectedItem(reviewItems[0])
            return
        }
        const freshItem = reviewItems.find(item => item.id === selectedItem.id)
        if (freshItem) {
            setSelectedItem(freshItem)
        } else {
            setSelectedItem(reviewItems[0])
        }
    }, [reviewItems]) // eslint-disable-line react-hooks/exhaustive-deps

    const hasDecisions = selectedItem ? selectedItem.decidedAssignments > 0 : false
    const allDecided = selectedItem ? selectedItem.decidedAssignments === selectedItem.totalAssignments : false

    const toggleChecked = useCallback((itemId: string) => {
        setCheckedItemIds(prev => {
            const next = new Set(prev)
            if (next.has(itemId)) {
                next.delete(itemId)
            } else {
                next.add(itemId)
            }
            return next
        })
    }, [])

    // Bulk certify is enabled when at least one item is checked and all checked items are fully decided + not completed
    const checkedCount = checkedItemIds.size
    const allCheckedFullyDecided = checkedCount > 0 && reviewItems
        .filter(item => checkedItemIds.has(item.id))
        .every(item => isFullyDecided(item) && item.status !== ReviewItemStatus.COMPLETED)
    const bulkCertifyEnabled = checkedCount > 0 && allCheckedFullyDecided

    // WHY: hide the bulk-select column when every row would render a disabled
    // checkbox — a column of greyed boxes the user can't act on reads as dead UI
    // (issue 970, option B). Reappears as soon as one row qualifies.
    const anyCheckboxSelectable = reviewItems.some(
        item => isFullyDecided(item) && item.status !== ReviewItemStatus.COMPLETED,
    )
    const tableColSpan = anyCheckboxSelectable ? 4 : 3

    const reviewStatusLabel = (status: ReviewItemStatus) => {
        switch (status) {
            case ReviewItemStatus.PENDING:
                return i18n.certificationReviews.statusPending
            case ReviewItemStatus.IN_REVIEW:
                return i18n.certificationReviews.statusInReview
            case ReviewItemStatus.COMPLETED:
                return i18n.certificationReviews.statusCompleted
            default:
                return status
        }
    }

    if (!campaigns) {
        return (
            <div>
                <MutedText>{i18n.common.loading}</MutedText>
            </div>
        )
    }

    if (error) {
        return (
            <div>
                <ErrorBanner error={error}/>
            </div>
        )
    }

    if (campaigns.length === 0) {
        return (
            <div>
                {!embedded && (
                    <PageBackButton/>
                )}
                <PageSectionHeader
                    icon={BarChart3}
                    title={i18n.certificationReviews.title}
                    description={i18n.certificationReviews.subtitle}
                />
                <EmptyState
                    variant="card"
                    icon={Users}
                    title={String(i18n.certificationReviews.noReviewsFound)}
                />

            </div>
        )
    }

    return (
        <div>
            {!embedded && (
                <PageBackButton/>
            )}
            <PageSectionHeader
                icon={BarChart3}
                title={i18n.certificationReviews.title}
                description={i18n.certificationReviews.subtitle}
            />

            <MasterDetailLayout
                data-testid="review-master-detail"
                masterTestId="review-left-panel"
                detailTestId="review-right-panel"
                master={
                    <>
                        <FilterToolbar>
                            <InlineRow justify="between">
                                <MutedText size="sm" data-testid="review-items-count">
                                    {i18n.certificationReviews.reviewItemsCount({count: totalElements})}
                                </MutedText>
                            </InlineRow>
                            <UnifiedSearchBar
                                contract={CertificationReviewControllerMyReviewItemsEndpoint}
                                value={searchAndFilters}
                                onChange={setSearchAndFilters}
                                placeholder={String(i18n.certificationReviews.searchPlaceholder)}
                            />
                            {/* Campaign quick-filter — page-specific UX above the unified bar
                                so reviewers can context-switch with one click. The selected
                                campaign is appended to the backend list query as `campaignId`. */}
                            <InlineRow wrap gap="sm" data-testid="campaign-quick-filter">
                                {campaigns.map((campaign) => (
                                    <PillButton
                                        key={campaign.id}
                                        selected={selectedCampaignId === campaign.id}
                                        onClick={() => setSelectedCampaignId(campaign.id)}
                                        data-testid={`campaign-filter-${campaign.id}`}
                                    >
                                        <span>{i18n.certificationReviews.campaign}: {campaign.name}</span>
                                        <span className={selectedCampaignId === campaign.id ? 'text-primary-foreground' : 'text-muted-foreground'}>{formatDateShort(campaign.dueDate)}</span>
                                    </PillButton>
                                ))}
                            </InlineRow>
                        </FilterToolbar>
                        <ScrollableBox grow flush>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        {anyCheckboxSelectable && <TableHead data-testid="col-expand" className="w-10"/>}
                                        <TableHead data-testid="col-identity">
                                            <SortableHeader label={i18n.certificationReviews.identity} sortKey={CertificationReviewControllerMyReviewItemsEndpoint.sort.IDENTITY_DISPLAY_NAME} currentSortBy={sortBy} currentSortDir={sortDir} onSort={setSort}/>
                                        </TableHead>
                                        <TableHead data-testid="col-status">
                                            <SortableHeader label={i18n.certificationReviews.status} sortKey={CertificationReviewItemResponseFields.status} currentSortBy={sortBy} currentSortDir={sortDir} onSort={setSort}/>
                                        </TableHead>
                                        <TableHead data-testid="col-decided-assignments" className="text-right">{i18n.certificationReviews.decidedAssignments}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoading && (
                                        <TableEmptyState
                                            colSpan={tableColSpan}
                                            hasFilters={false}
                                            matchMessage={String(i18n.common.loading)}
                                            emptyMessage={String(i18n.common.loading)}
                                        />
                                    )}
                                    {!isLoading && reviewItems.length === 0 && (
                                        <TableEmptyState
                                            colSpan={tableColSpan}
                                            hasFilters={hasActiveFilters}
                                            matchMessage={i18n.certificationReviews.noReviewsFound}
                                            emptyMessage={i18n.certificationReviews.noReviewsFound}
                                        />
                                    )}
                                    {!isLoading && reviewItems.map((item) => {
                                        const fullyDecided = isFullyDecided(item)
                                        const checkboxEnabled = fullyDecided && item.status !== ReviewItemStatus.COMPLETED
                                        return (
                                            <TableRow
                                                key={item.id}
                                                className={selectedItem?.id === item.id ? ROW_SELECTED_CLASS : ROW_UNSELECTED_CLASS}
                                                onClick={() => setSelectedItem(item)}
                                                data-testid={`review-item-${item.id}`}
                                            >
                                                {anyCheckboxSelectable && (
                                                    <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox
                                                            checked={checkedItemIds.has(item.id)}
                                                            onCheckedChange={() => toggleChecked(item.id)}
                                                            disabled={!checkboxEnabled}
                                                            data-testid={`review-item-checkbox-${item.id}`}
                                                        />
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <InlineRow gap="md">
                                                        <Strong data-testid={`review-item-name-${item.id}`}>{item.identity.displayName}</Strong>
                                                        {item.selfCertification && (
                                                            <Badge variant="warning" className="text-xs" data-testid={`review-item-self-cert-${item.id}`}>{i18n.certificationReviews.selfCertification}</Badge>
                                                        )}
                                                    </InlineRow>
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const reviewBadge = ReviewItemStatusBadge.of(item.status)
                                                        const ReviewIcon = reviewBadge?.icon
                                                        return (
                                                            <Badge variant={reviewBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1 text-xs" data-testid={`review-item-status-${item.id}`}>
                                                                {ReviewIcon && <ReviewIcon className="h-3 w-3" aria-hidden="true"/>}
                                                                {reviewStatusLabel(item.status)}
                                                            </Badge>
                                                        )
                                                    })()}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <MutedText size="sm">{item.decidedAssignments}/{item.totalAssignments}</MutedText>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </ScrollableBox>
                    </>
                }
                masterFooter={
                    <>
                        {checkedCount > 0 && (
                            <BulkActionBar>
                                <MutedText size="xs">
                                    {i18n.common.selectedCount({count: checkedCount})}
                                </MutedText>
                                <Button
                                    size="sm"
                                    disabled={!bulkCertifyEnabled}
                                    onClick={bulkCertifyDialog.open}
                                    data-testid="btn-bulk-certify"
                                >
                                    <Icon icon={CheckSquare} className="mr-1"/>
                                    {i18n.certificationReviews.bulkCertify}
                                </Button>
                            </BulkActionBar>
                        )}
                        {totalPages > 0 && (
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalPages={totalPages}
                                totalItems={totalElements}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={pageSizeOptions}
                                compact
                            />
                        )}
                    </>
                }
                detail={
                    !selectedItem ? (
                        <EmptyState
                            icon={Users}
                            title={String(i18n.certificationReviews.noReviewsFound)}
                        />
                    ) : (
                        <Stack gap="lg">
                            {/* Identity Header */}
                            <InlineRow align="start" justify="between">
                                <Stack gap="xs">
                                    <Strong className="text-xl" data-testid="review-detail-name">{selectedItem.identity.displayName}</Strong>
                                    {selectedItem.reviewer && (
                                        <MutedText size="sm" block data-testid="review-detail-reviewer">
                                            {i18n.certificationReviews.reviewer}: {selectedItem.reviewer.displayName}
                                        </MutedText>
                                    )}
                                    <MutedText size="sm" block data-testid="review-detail-campaign">
                                        {i18n.certificationReviews.campaign}: {selectedItem.campaignName}
                                        {/* eslint-disable-next-line i18next/no-literal-string -- separator character */}
                                        {' \u00B7 '}
                                        {i18n.certificationReviews.dueDate}: {formatDateShort(selectedItem.campaignDueDate)}
                                    </MutedText>
                                </Stack>
                                {(() => {
                                    const reviewBadge = ReviewItemStatusBadge.of(selectedItem.status)
                                    const ReviewIcon = reviewBadge?.icon
                                    return (
                                        <Badge variant={reviewBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1 whitespace-nowrap">
                                            {ReviewIcon && <ReviewIcon className="h-3 w-3" aria-hidden="true"/>}
                                            {reviewStatusLabel(selectedItem.status)}
                                        </Badge>
                                    )
                                })()}
                            </InlineRow>

                            {/* Progress Chart */}
                            <Card>
                                <CardContent className="pt-6">
                                    <Stack gap="sm" data-testid="review-progress">
                                        <InlineRow justify="between" className="text-sm">
                                            <InlineRow gap="md">
                                                <Strong>{i18n.certificationReviews.decidedAssignments}</Strong>
                                                {selectedItem.selfCertification && (
                                                    <Badge variant="warning" className="text-xs">{i18n.certificationReviews.selfCertification}</Badge>
                                                )}
                                            </InlineRow>
                                            <MutedText data-testid="stat-decided">
                                                {selectedItem.decidedAssignments} / {selectedItem.totalAssignments}
                                            </MutedText>
                                        </InlineRow>
                                        <Progress
                                            value={selectedItem.totalAssignments > 0
                                                ? (selectedItem.decidedAssignments / selectedItem.totalAssignments) * 100
                                                : 0}
                                            className="h-3"
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Actions */}
                            <InlineRow justify="end" gap="md">
                                {selectedItem.status !== ReviewItemStatus.COMPLETED && (
                                    <Button
                                        variant="outline"
                                        onClick={reassignDialog.open}
                                        data-testid="btn-reassign-item"
                                    >
                                        {i18n.certificationReviews.reassign}
                                    </Button>
                                )}
                                <Button
                                    variant={hasDecisions ? 'outline' : 'default'}
                                    // eslint-disable-next-line i18next/no-literal-string
                                    onClick={() => navigate(`${selectedItem.id}/assignments`, {state: {campaignId: selectedCampaignId}})}
                                    data-testid="btn-review-assignments"
                                >
                                    {hasDecisions
                                        ? i18n.certificationReviews.continueReview
                                        : i18n.certificationReviews.startReview}
                                </Button>
                                {hasDecisions && selectedItem.status !== ReviewItemStatus.COMPLETED && (
                                    <Button
                                        onClick={certifyDialog.open}
                                        disabled={!allDecided}
                                        data-testid="btn-certify-item"
                                    >
                                        {i18n.certificationReviews.certify}
                                    </Button>
                                )}
                            </InlineRow>
                        </Stack>
                    )
                }
            />

            {/* Certify Confirmation Dialog */}
            <Dialog open={certifyDialog.isOpen} onOpenChange={certifyDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationReviews.certify}</DialogTitle>
                        <DialogDescription>
                            {selectedItem?.identity.displayName}
                        </DialogDescription>
                    </DialogHeader>
                    {certifyMutation.error && <ErrorBanner error={certifyMutation.error}/>}
                    <SaveCancelFooter
                        testIdPrefix="certify"
                        onCancel={certifyDialog.close}
                        saving={certifyMutation.isPending}
                        saveLabel={i18n.certificationReviews.certify}
                        saveType="button"
                        onSave={() => selectedItem && certifyMutation.mutate(selectedItem.id)}
                    />
                </DialogContent>
            </Dialog>

            {/* Bulk Certify Confirmation Dialog */}
            <Dialog open={bulkCertifyDialog.isOpen} onOpenChange={bulkCertifyDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationReviews.bulkCertify}</DialogTitle>
                        <DialogDescription>
                            {i18n.certificationReviews.bulkCertifyDescription({count: checkedCount})}
                        </DialogDescription>
                    </DialogHeader>
                    {bulkCertifyMutation.error && <ErrorBanner error={bulkCertifyMutation.error}/>}
                    <SaveCancelFooter
                        testIdPrefix="bulk-certify"
                        onCancel={bulkCertifyDialog.close}
                        saving={bulkCertifyMutation.isPending}
                        saveLabel={i18n.certificationReviews.bulkCertifyCount({count: checkedCount})}
                        saveType="button"
                        onSave={() => bulkCertifyMutation.mutate(Array.from(checkedItemIds))}
                    />
                </DialogContent>
            </Dialog>

            {/* Reassign Dialog */}
            <Dialog open={reassignDialog.isOpen} onOpenChange={(open) => {
                reassignDialog.bind.onOpenChange(open)
                if (!open) {
                    setReassignIdentityId(null)
                    setIdentitySearchQuery('')
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationReviews.reassignTo}</DialogTitle>
                        <DialogDescription>
                            {i18n.certificationReviews.reassignDescription}
                        </DialogDescription>
                    </DialogHeader>
                    <Combobox
                        options={identityOptions}
                        value={reassignIdentityId}
                        onValueChange={setReassignIdentityId}
                        onSearch={setIdentitySearchQuery}
                        placeholder={i18n.certificationReviews.searchIdentity}
                        emptyMessage={i18n.certificationCampaigns.noIdentitiesFound}
                        isLoading={identitySearchLoading}
                        className="w-full"
                    />
                    {reassignMutation.error && <ErrorBanner error={reassignMutation.error}/>}
                    <SaveCancelFooter
                        testIdPrefix="reassign"
                        onCancel={reassignDialog.close}
                        saving={reassignMutation.isPending}
                        disabled={!reassignIdentityId}
                        saveLabel={i18n.certificationReviews.reassign}
                        saveType="button"
                        onSave={() => selectedItem && reassignIdentityId && reassignMutation.mutate({
                            reviewItemId: selectedItem.id,
                            newReviewerIdentityId: reassignIdentityId,
                        })}
                    />
                </DialogContent>
            </Dialog>

        </div>
    )
}
